import React from "react";
import { Table, Button, Tooltip, Popconfirm, Empty } from "antd";
import {
  DeleteOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "./styles.css";

function DataTable({
  data,
  meta,
  loading,
  onPageChange,
  onApprove,
  onDelete,
  getStatusTag,
  getRedeemableTag,
}) {
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (user) => (
        <div className="loyalty-user-info">
          <div className="loyalty-user-name">{user?.name || "N/A"}</div>
          <div className="loyalty-user-email">{user?.email || "N/A"}</div>
        </div>
      ),
    },
    {
      title: "Points",
      dataIndex: "points",
      key: "points",
      width: 100,
      render: (points) => (
        <div className="loyalty-points-badge">
          <GiftOutlined style={{ marginRight: 4 }} />
          {points || 0} pts
        </div>
      ),
      sorter: (a, b) => (a.points || 0) - (b.points || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <div className="loyalty-status-tag">{getStatusTag(status)}</div>
      ),
      filters: [
        { text: "Pending", value: "pending" },
        { text: "Approved", value: "approved" },
        { text: "Rejected", value: "rejected" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Redeemable",
      dataIndex: "isRedeemable",
      key: "isRedeemable",
      width: 140,
      render: (isRedeemable) => (
        <div className="loyalty-status-tag">
          {getRedeemableTag(isRedeemable)}
        </div>
      ),
      filters: [
        { text: "Redeemable", value: true },
        { text: "Not Redeemable", value: false },
      ],
      onFilter: (value, record) => record.isRedeemable === value,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (description) => (
        <Tooltip title={description}>
          <span>{description || "N/A"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date) => moment(date).format("DD/MM/YYYY HH:mm"),
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 150,
      render: (date) => moment(date).format("DD/MM/YYYY HH:mm"),
      sorter: (a, b) => moment(a.updatedAt).unix() - moment(b.updatedAt).unix(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      fixed: "right",
      render: (_, record) => (
        <div className="loyalty-actions">
          <Tooltip title="View Details">
            <Button
              className="loyalty-action-btn view"
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>

          {record.status === "pending" && (
            <Tooltip title="Approve">
              <Button
                className="loyalty-action-btn approve"
                type="text"
                icon={<CheckCircleOutlined />}
                size="small"
                onClick={() => onApprove(record.id)}
              />
            </Tooltip>
          )}

          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this record?"
              onConfirm={() => onDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                className="loyalty-action-btn delete"
                type="text"
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleView = (record) => {
    // You can implement a modal to show detailed information
    console.log("View record:", record);
  };

  const pagination = {
    current: meta?.currentPage || 1,
    pageSize: meta?.itemsPerPage || 10,
    total: meta?.itemCount || 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    pageSizeOptions: ["10", "20", "50", "100"],
    onChange: onPageChange,
    onShowSizeChange: onPageChange,
  };

  return (
    <div className="loyalty-table-container">
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        scroll={{ x: 1200 }}
        size="middle"
        bordered
        locale={{
          emptyText: (
            <Empty
              image={
                <GiftOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />
              }
              description="No loyalty records found"
            />
          ),
        }}
      />
    </div>
  );
}

export default DataTable;
